import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, User } from "lucide-react";
import { API } from "../../lib/site";

const inputCls =
  "w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition";
const labelCls = "block font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500 mb-1.5";

export default function UsersTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios
      .get(`${API}/admin/users`, { withCredentials: true })
      .then((res) => setUsers(res.data.users || []))
      .catch(() => {});
  }, []);

  useEffect(() => load(), [load]);

  const add = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users`, form, { withCredentials: true });
      toast.success(`User ${form.email} added as ${form.role === "admin" ? "admin" : "team member"}.`);
      setForm({ name: "", email: "", password: "", role: "member" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add user.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, email) => {
    try {
      await axios.delete(`${API}/admin/users/${id}`, { withCredentials: true });
      toast.success(`Removed ${email}.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove user.");
    }
  };

  return (
    <div data-testid="admin-users-tab" className="max-w-3xl space-y-6">
      <div className="bg-white border border-sand-border rounded-2xl p-6 sm:p-8">
        <h3 className="font-serif text-2xl text-slate-900">Add a team member</h3>
        <p className="mt-2 text-sm text-slate-600">
          Only you ({currentUser?.email}) can add or remove users. Choose what the new person can do:
        </p>
        <form onSubmit={add} className="mt-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Name</label>
              <input data-testid="admin-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aisha" className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" data-testid="admin-user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="team@reefdeco.com" className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>Password (min 8 chars)</label>
              <input type="text" data-testid="admin-user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Set a password" className={inputCls} required />
            </div>
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                ["member", "Team member", "Can view & manage bookings and estimate leads only."],
                ["editor", "Content editor", "Can edit site content and blog articles only."],
                ["admin", "Admin", "Full access: bookings, leads, site content, blog & integrations."],
              ].map(([id, label, hint]) => (
                <button
                  key={id}
                  type="button"
                  data-testid={`admin-user-role-${id}`}
                  onClick={() => setForm({ ...form, role: id })}
                  className={`rounded-xl border px-4 py-3.5 text-left transition-all duration-200 ${
                    form.role === id ? "border-reef bg-reef-light" : "border-sand-border hover:border-reef/40"
                  }`}
                >
                  <p className={`text-sm font-semibold ${form.role === id ? "text-reef" : "text-slate-800"}`}>{label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{hint}</p>
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            data-testid="admin-user-add-btn"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-reef text-sand-bg font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-reef-dark transition-colors disabled:opacity-60"
          >
            <UserPlus className="w-4 h-4" /> {saving ? "Adding…" : "Add user"}
          </button>
        </form>
      </div>

      <div className="space-y-3">
        {users.map((u, i) => (
          <div key={u.id} data-testid={`admin-user-row-${i}`} className="bg-white border border-sand-border rounded-2xl p-5 flex items-center gap-4">
            <span className="w-10 h-10 rounded-full bg-reef-light text-reef font-serif text-lg flex items-center justify-center shrink-0">
              {(u.name || u.email || "U")[0].toUpperCase()}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 truncate">{u.name}</p>
              <p className="font-mono text-[11px] text-slate-400 truncate">{u.email}</p>
            </div>
            <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full border font-mono text-[10px] tracking-[0.1em] uppercase shrink-0 ${
              u.role === "admin" ? "border-reef/25 bg-reef-light text-reef" : "border-slate-200 bg-slate-50 text-slate-500"
            }`}>
              {u.role === "admin" ? <ShieldCheck className="w-3 h-3" /> : <User className="w-3 h-3" />}
              {u.role === "admin" ? "Admin" : u.role === "editor" ? "Editor" : "Member"}
            </span>
            {currentUser?.id !== u.id && (
              <button
                data-testid={`admin-user-delete-${i}`}
                onClick={() => remove(u.id, u.email)}
                className="p-2 text-slate-300 hover:text-red-600 transition-colors"
                aria-label={`Remove ${u.email}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
